import {
  CellClassParams,
  CellStyleFunc,
  RowClassParams,
  RowStyle,
  ValueFormatterFunc,
  ValueFormatterParams,
} from "ag-grid-community"

import { SCIENT_COLORS } from "../../../../styled/scientColors"
import { Mask, OrderSide, PositionSide } from "../types"
import { getOrderSideStyle, normalizeToSide } from "../Trading/utils/sideStyle"

export enum RowStyleProtocol {
  HIGHLIGHTED_BLACK_BOLD = "highlightedBlackBold",
  ORDER_FAILURE_WARNING = "orderFailureWarning",
  ORDER_LOCATE_PENDING = "orderLocatePending",
}

export enum CellStyleProtocol {
  RED_AND_GREEN_BOLD = "redAndGreenBold",
  WHITE_BOLD = "whiteBold",
  WHITE = "white",
  POSITION_SIDE = "positionSide",
  ORDER_SIDE = "orderSide",
  MEDIUM_TEXT_CENTERED = "mediumTextCentered",
  GREEN_ABOVE_THRESHOLD = "greenAboveThreshold",
  GREEN_BELLOW_THRESHOLD = "greenBellowThreshold",
  PRICE_SOURCE_STATUS = "priceSourceStatus",
  PERCENTAGE_COMPLETION = "percentageCompletion",
  Z_SCORE = "zScore",
  FORECASTED = "forecasted",
  BACKCASTED = "backcasted",
  FUNDAMENTAL_FORECASTED = "fundamentalForecasted",
  FUNDAMENTAL_FORECASTED_FY = "fundamentalForecastedFY",
}

export type ScientCellStyle = {
  style: CellStyleProtocol
  threshold?: number
  maskName?: string
}

export enum ScientValueFormatter {
  PERCENTAGE = "percentage",
  INT_PERCENTAGE = "int_percentage",
  NUMBER_GB = "number_gb",
  NUMBER_GB_1ROUND = "number_gb_1round",
  INT_GB = "int_gb",
  TEXT = "text",
  TEXT_CAPITALIZED = "textCapitalized",
  DAY_HOUR = "day_hour",
  DATE = "date",
}

export enum AgGridFilter {
  AG_NUMBER = "agNumberColumnFilter",
  AG_SET = "agSetColumnFilter",
}

/**
 * Below is for cell styling
 */
const mediumTextCenteredStrategy = (params: CellClassParams) => {
  return { display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px" }
}

const redAndGreenStrategy = (params: CellClassParams) => {
  return params.value > 0
    ? { color: SCIENT_COLORS.forest3, fontWeight: "bold" }
    : params.value < 0
      ? { color: SCIENT_COLORS.vermilion4, fontWeight: "bold" }
      : undefined
}

const whiteBoldStrategy = (params: CellClassParams) => {
  return { color: SCIENT_COLORS.white, fontWeight: "bold" }
}

const whiteStrategy = (params: CellClassParams) => {
  return { color: SCIENT_COLORS.white }
}

/**
 * priceSourceStatusStrategy is specific to displaying prices based on source and status
 * - Bloomberg live prices: Blue
 * - Bloomberg delayed prices: Violet
 * - External prices: White
 */
const priceSourceStatusStrategy = (params: CellClassParams) => {
  // Get the data for the current row
  const rowData = params.data || {}

  // Check price source and status

  if (rowData["price_status"] === "live") {
    return { color: SCIENT_COLORS.blue3, fontWeight: "bold" }
  } else if (rowData["price_status"] === "delayed") {
    return { color: SCIENT_COLORS.violet5, fontWeight: "bold" }
  }

  // Default to white for external or other sources
  return { color: SCIENT_COLORS.white, fontWeight: "normal" }
}

/**
 * positionSideStrategy is specific to Trading
 */
const positionSideStrategy = (params: CellClassParams) => {
  switch (params.value) {
    case PositionSide.LONG:
      return {
        backgroundColor: SCIENT_COLORS.green3,
        color: SCIENT_COLORS.white,
        fontWeight: "bold",
      }
    case PositionSide.SHORT:
      return {
        backgroundColor: SCIENT_COLORS.gold3,
        color: SCIENT_COLORS.white,
        fontWeight: "bold",
      }
    default:
      return undefined
  }
}

/**
 * orderSideStrategy is specific to Trading
 */
const orderSideStrategy = (params: CellClassParams) => {
  // params.value may be the display value (e.g., "Buy") or the enum string value (e.g., "buy")
  const side = (Object.values(OrderSide) as Array<string>).includes(params.value)
    ? normalizeToSide(String(params.value))
    : undefined
  return getOrderSideStyle(side as any)
}

const greenAboveThresholdStrategy = (params: CellClassParams, cellStyleThreshold: number) => {
  return params.value > cellStyleThreshold
    ? { color: SCIENT_COLORS.forest3, fontWeight: "bold" }
    : undefined
}

const greenBelowThresholdStrategy = (params: CellClassParams, cellStyleThreshold: number) => {
  return params.value < cellStyleThreshold
    ? { color: SCIENT_COLORS.forest3, fontWeight: "bold" }
    : undefined
}

const percentageCompletionStrategy = (params: CellClassParams) => {
  const percentage = Math.min(Math.max(params.value || 0, 0), 100)
  const isComplete = percentage === 100

  // Use a consistent background color with gradient overlay
  const progressColor = isComplete ? SCIENT_COLORS.forest3 : SCIENT_COLORS.gray3
  const backgroundColor = SCIENT_COLORS.darkGray2

  return {
    backgroundColor: backgroundColor,
    background: `linear-gradient(to right, ${progressColor} ${percentage}%, transparent ${percentage}%)`,
    color: SCIENT_COLORS.white,
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  }
}

const forecastedStrategy = (params: CellClassParams) => {
  return { border: `1px solid ${SCIENT_COLORS.blue3}` }
}

const backcastedStrategy = (params: CellClassParams) => {
  return { border: `1px solid ${SCIENT_COLORS.orange1}` }
}

const fundamentalForecastedStrategy = (params: CellClassParams) => {
  return { backgroundColor: `${SCIENT_COLORS.orange1}99` } // Last 2 digits are opacity
}

const fundamentalForecastedFYStrategy = (params: CellClassParams) => {
  return {
    backgroundColor: SCIENT_COLORS.orange1,

    borderLeft: `2px solid ${SCIENT_COLORS.orange3}`,
    borderRight: `2px solid ${SCIENT_COLORS.orange3}`,
  }
}

/**
 * Cell style strategy for masked cells
 */
const maskedCellStyleStrategy = (
  params: CellClassParams,
  maskName: string,
  style: CellStyleProtocol,
  masks: Record<string, Mask[]> | undefined,
) => {
  const mask = masks?.[maskName]

  if (mask) {
    const rowIdx = params.data?._idx ?? params.node?.rowIndex ?? -1
    const maskRow = mask.find(m => (m as any)._idx === rowIdx)
    const fields: unknown[] | undefined = (maskRow as any)?.fields
    const colField = params.colDef.field ? String(params.colDef.field) : undefined
    const datePrefixRe = /^(\d{4}-\d{2}-\d{2})/
    if (colField && Array.isArray(fields)) {
      const colIsDate = datePrefixRe.test(colField)
      const hasMatch = fields.some(f => {
        const s = String(f)
        const fieldIsDate = datePrefixRe.test(s)
        if (colIsDate && fieldIsDate) {
          return s.slice(0, 10) === colField.slice(0, 10)
        }
        return s === colField
      })
      if (hasMatch) {
        return columnsDefCellStyleMapping[style]?.(params)
      }
    }
  }
}

// Helper function to convert hex to rgba
const hexToRgba = (hex: string, opacity: number): string => {
  // Remove the hash if present
  const cleanHex = hex.replace("#", "")

  // Convert hex to rgb
  const r = parseInt(cleanHex.substring(0, 2), 16)
  const g = parseInt(cleanHex.substring(2, 4), 16)
  const b = parseInt(cleanHex.substring(4, 6), 16)

  // Return rgba string
  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

const zScoreStrategy = (params: CellClassParams) => {
  const zScore = params.value || 0

  // No background if z-score is 0
  if (zScore === 0) return undefined

  // Calculate opacity based on absolute z-score value
  // Cap at 3 standard deviations for max opacity
  const opacity = Math.min(Math.abs(zScore) / 3, 1)

  // Choose color based on z-score sign
  const baseColor = zScore > 0 ? SCIENT_COLORS.forest3 : SCIENT_COLORS.vermilion3

  // Make last row extra bold
  const isLastRow = params.node.lastChild

  return {
    backgroundColor: hexToRgba(baseColor, opacity),
    color: SCIENT_COLORS.white,
    fontWeight: isLastRow ? "900" : "normal",
    ...(isLastRow && {
      borderTop: "1px solid white",
      fontSize: "1.1em",
    }),
  }
}

export const columnsDefCellStyleMapping: Partial<{ [key in CellStyleProtocol]: CellStyleFunc }> = {
  [CellStyleProtocol.RED_AND_GREEN_BOLD]: redAndGreenStrategy,
  [CellStyleProtocol.WHITE_BOLD]: whiteBoldStrategy,
  [CellStyleProtocol.WHITE]: whiteStrategy,
  [CellStyleProtocol.POSITION_SIDE]: positionSideStrategy,
  [CellStyleProtocol.ORDER_SIDE]: orderSideStrategy,
  [CellStyleProtocol.MEDIUM_TEXT_CENTERED]: mediumTextCenteredStrategy,
  [CellStyleProtocol.PRICE_SOURCE_STATUS]: priceSourceStatusStrategy,
  [CellStyleProtocol.PERCENTAGE_COMPLETION]: percentageCompletionStrategy,
  [CellStyleProtocol.Z_SCORE]: zScoreStrategy,
  [CellStyleProtocol.FORECASTED]: forecastedStrategy,
  [CellStyleProtocol.BACKCASTED]: backcastedStrategy,
  [CellStyleProtocol.FUNDAMENTAL_FORECASTED]: fundamentalForecastedStrategy,
  [CellStyleProtocol.FUNDAMENTAL_FORECASTED_FY]: fundamentalForecastedFYStrategy,
}

const makeStrategy = (
  style: CellStyleProtocol,
  threshold?: number,
  maskName?: string,
  masks?: Record<string, Mask[]> | undefined,
): CellStyleFunc | undefined => {
  if (style === CellStyleProtocol.GREEN_ABOVE_THRESHOLD) {
    return (params: CellClassParams) => {
      const t = threshold ?? 0
      return greenAboveThresholdStrategy(params, t)
    }
  }
  if (style === CellStyleProtocol.GREEN_BELLOW_THRESHOLD) {
    return (params: CellClassParams) => {
      const t = threshold ?? 0
      return greenBelowThresholdStrategy(params, t)
    }
  }
  if (
    (style === CellStyleProtocol.FORECASTED || style === CellStyleProtocol.BACKCASTED) &&
    maskName
  ) {
    return (params: CellClassParams) => {
      return maskedCellStyleStrategy(params, maskName, style, masks)
    }
  }
  return columnsDefCellStyleMapping[style] // may be undefined
}

/**
 * Combine multiple cell styles into a single cell style function
 */
export const combineCellStyles =
  (...fns: CellStyleFunc[]): CellStyleFunc =>
  params =>
    fns.reduce((acc, fn) => Object.assign(acc, fn(params) ?? {}), {} as Record<string, any>)

/**
 * Get the cell style pipeline for a given style descriptor
 */
export const getCellStylePipeline = (
  styles: (ScientCellStyle | CellStyleProtocol)[] | undefined,
  masks: Record<string, Mask[]> | undefined,
): CellStyleFunc | undefined => {
  if (!styles) return undefined

  const arr = Array.isArray(styles) ? styles : [styles]

  const fns = arr
    .map(s =>
      typeof s === "string"
        ? makeStrategy(s)
        : makeStrategy(s.style, s.threshold, s.maskName, masks),
    )
    .filter((fn): fn is CellStyleFunc => Boolean(fn))

  if (fns.length === 0) return undefined
  if (fns.length === 1) return fns[0]

  return combineCellStyles(...fns)
}

/**
 * Below is for value formatter
 */
const numberGBFormatter = (params: ValueFormatterParams) => {
  if (typeof params.value === "number") {
    return Intl.NumberFormat("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(params.value)
  } else return "-"
}

const numberGB1roundFormatter = (params: ValueFormatterParams) => {
  if (typeof params.value === "number") {
    return Intl.NumberFormat("en-GB", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(params.value)
  } else return "-"
}

const intGBFormatter = (params: ValueFormatterParams) => {
  if (typeof params.value === "number") {
    return Intl.NumberFormat("en-GB").format(+params.value.toFixed(0))
  } else return "-"
}

const percentageFormatter = (params: ValueFormatterParams) => {
  return typeof params.value === "number"
    ? `${Intl.NumberFormat("en-GB").format(+params.value.toFixed(2))} %`
    : "-"
}

const intPercentageFormatter = (params: ValueFormatterParams) => {
  return typeof params.value === "number"
    ? `${Intl.NumberFormat("en-GB").format(+params.value.toFixed(1))} %`
    : "-"
}

const identityFormatter = (params: ValueFormatterParams) => {
  return params.value
}

const textCapitalizedFormatter = (params: ValueFormatterParams) => {
  return params.value?.toUpperCase()
}

const dayHourDateFormatter = (params: ValueFormatterParams) => {
  if (!params.value) return "-"
  const date = new Date(params.value)
  if (isNaN(date.getTime())) return "-"
  return new Intl.DateTimeFormat("en", {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date)
}

const dateFormatter = (params: ValueFormatterParams) => {
  if (!params.value) return "-"
  const date = new Date(params.value)
  if (isNaN(date.getTime())) return "-"
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date)
}

export const columnsDefValueFormatterMapping: {
  [key in ScientValueFormatter]: string | ValueFormatterFunc
} = {
  [ScientValueFormatter.NUMBER_GB]: numberGBFormatter,
  [ScientValueFormatter.NUMBER_GB_1ROUND]: numberGB1roundFormatter,
  [ScientValueFormatter.PERCENTAGE]: percentageFormatter,
  [ScientValueFormatter.INT_PERCENTAGE]: intPercentageFormatter,
  [ScientValueFormatter.TEXT]: identityFormatter,
  [ScientValueFormatter.TEXT_CAPITALIZED]: textCapitalizedFormatter,
  [ScientValueFormatter.INT_GB]: intGBFormatter,
  [ScientValueFormatter.DAY_HOUR]: dayHourDateFormatter,
  [ScientValueFormatter.DATE]: dateFormatter,
}

/**
 * Below is for row styling
 */

const highlightedBlackBoldRowStyle: RowStyle = {
  backgroundColor: SCIENT_COLORS.darkGray1,
  fontWeight: "bold",
}

const orderFailureWarningStrategy: RowStyleFunc = (params: RowClassParams) => {
  if (["Rejected", "Locate Failed"].includes(params.data["status"])) {
    return {
      backgroundColor: SCIENT_COLORS.gold4,
      fontWeight: "bold",
    }
  }
}

const orderLocatePendingStrategy: RowStyleFunc = (params: RowClassParams) => {
  if (params.data["status"] === "Locate Pending") {
    return {
      backgroundColor: SCIENT_COLORS.cerulean3,
    }
  }
}

export interface RowStyleFunc {
  (params: RowClassParams): RowStyle | undefined
}

export const rowStyleMapping: { [key in RowStyleProtocol]: RowStyleFunc } = {
  [RowStyleProtocol.HIGHLIGHTED_BLACK_BOLD]: (params: RowClassParams) =>
    highlightedBlackBoldRowStyle,
  [RowStyleProtocol.ORDER_FAILURE_WARNING]: orderFailureWarningStrategy,
  [RowStyleProtocol.ORDER_LOCATE_PENDING]: orderLocatePendingStrategy,
}
